<script lang="ts" setup>
import { useI18n } from 'vue-i18n';
import { useAsyncData } from '@/hooks/async-data';
import { useRouter } from 'vue-router';
import { $fetch } from '@/plugins/fetch';
import type { PublicCaseStudyPageData, PublicCaseStudyListItem } from '@/views/case-study/types';

defineOptions({
  name: 'CaseStudySection',
});

const { t } = useI18n();
const router = useRouter()
const { data: listData } = useAsyncData('home:case-study',
  () => $fetch<PublicCaseStudyPageData>('/case/research/v1/recommend/list', { data: { limit: 6 } }),
  {
    transform: (data) => data ?? [],
  },
);

const handleClick = (item: PublicCaseStudyListItem) => {
  if (item.detailType === 0) {
    return
  }
  else if (item.detailType === 1) {
    router.push(`/case-study/${item.id}`)
  } else {
    window.open(item.externalUrl)
  }
}
</script>

<template>
  <section v-if="listData?.length > 0" class="page-home-case-study bg-#F7F8FA py-80 <md:py-40">
    <div class="container w-1200 <md:w-full <sm:px-16 <xl:px-24">
      <div class="text-center">
        <h2 class="m-0 text-40 text-#292D33 font-600 leading-56 <md:text-28">
          {{ t('home.caseStudy.title') }}
        </h2>
        <p class="m-0 mt-8 text-18 text-#9497A1 font-400 leading-30 <md:text-14">
          {{ t('home.caseStudy.subtitle') }}
        </p>
      </div>

      <ul class="grid grid-cols-3 m-0 mt-48 gap-24 p-0 <lg:grid-cols-2 <md:grid-cols-1">
        <li v-for="item in listData" :key="item.id" class="case-card relative cursor-pointer overflow-hidden rounded-4 bg-white shadow-[0_0_16px_0_rgba(0,0,0,0.08)]" @click="handleClick(item)">
          <div class="relative h-180 overflow-hidden">
            <img :src="item.imageUrl" :alt="item.name" class="h-full w-full object-cover">
            <span class="pointer-events-none absolute bottom-12 right-12 rounded-2 bg-[rgba(0,0,0,0.48)] px-8 py-4 text-12 text-white leading-16">
              {{ t('home.caseStudy.aiGenerated') }}
            </span>
          </div>

          <div class="p-24">
            <div class="flex flex-wrap gap-8">
              <span v-for="group in item.industryList" :key="group.id" class="rounded-2 bg-[rgba(17,128,255,0.08)] px-8 py-4 text-12 color-brand-primary">
                {{ group.name }}
              </span>
              <span v-for="group in item.projectList" :key="group.id" class="rounded-2 bg-[rgba(45,45,45,0.04)] px-8 py-4 text-12 color-#2d2d2d">
                {{ group.name }}
              </span>
            </div>
            <h3 class="line-clamp-2 m-0 mt-8 h-52 text-18 color-#2d2d2d font-500 leading-26">
              {{ item.name }}
            </h3>

            <div class="mt-8 h-36">
              <p class="m-0 truncate text-14 color-#9A9EA6" v-if="item.partyA">{{ t('home.caseStudy.partyA') }}{{ item.partyA }}</p>
              <p class="m-0 mt-4 truncate text-14 color-#9A9EA6" v-if="item.partner">{{ t('home.caseStudy.partner') }}{{ item.partner }}</p>
            </div>

            <div class="mt-8 flex items-center justify-between">
              <span class="truncate text-14 text-#9A9EA6 no-underline">
                {{ t('home.caseStudy.contact') }}{{ item.project.email }}
              </span>
            </div>

            <div v-if="item.detailType" class="mt-16 flex items-center text-14 color-brand-primary">
              <span>{{ t('home.caseStudy.viewDetail') }}</span>
              <span class="i-home-icons:arrow-right ml-4 size-16"></span>
            </div>
          </div>
        </li>
      </ul>

      <div class="mt-48 text-center">
        <RouterLink to="/case-study" class="inline-flex items-center gap-x-8 text-16 text-#1281FF font-500 no-underline hover:opacity-80">
          <span>{{ t('home.caseStudy.viewAll') }}</span>
          <span class="i-home-icons:arrow-right-2 block size-24"></span>
        </RouterLink>
      </div>
    </div>
  </section>
</template>

<style lang="scss" scoped>
.case-card {
  border: 1px solid rgba(154, 158, 166, 0.1);
}
</style>
